import axios from 'axios';
import { API_BASE_URL, slidingSessionConfig } from './config';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

axiosInstance.interceptors.request.use(config => {
  const token = localStorage.getItem(slidingSessionConfig.tokenKey);
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

axiosInstance.interceptors.response.use(
  res => res,
  async err => {
    if (err.response?.status === 401) {
      localStorage.removeItem(slidingSessionConfig.tokenKey);
      localStorage.removeItem(slidingSessionConfig.userKey);
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth-expired'));
    }
    return Promise.reject(err);
  }
);

// Auth
export const authService = {
  login: (email, password) => axiosInstance.post('/auth/login', { email, password }).then(r => r.data),
  register: (data) => axiosInstance.post('/auth/register', data).then(r => r.data),
  validateToken: () => axiosInstance.get('/auth/validate').then(r => r.data),
  refreshToken: () => axiosInstance.post('/auth/refresh-token').then(r => r.data)
};

// Sevkiyatlar
export const sevkiyatService = {
  getSevkiyatlar: () => axiosInstance.get('/sevkiyatlar').then(r => r.data),
  getTumSevkiyatlar: (params) => axiosInstance.get('/sevkiyatlar/tumu', { params }).then(r => r.data),
  getGecmisSevkiyatlar: () => axiosInstance.get('/sevkiyatlar/gecmis').then(r => r.data),
  getSevkiyatById: (id) => axiosInstance.get(`/sevkiyatlar/${id}`).then(r => r.data),
  addSevkiyat: (data) => axiosInstance.post('/sevkiyatlar', data).then(r => r.data),
  updateSevkiyat: (id, data) => axiosInstance.put(`/sevkiyatlar/${id}`, data).then(r => r.data),
  completeSevkiyat: (id, notlar) => axiosInstance.patch(`/sevkiyatlar/${id}/tamamla`, { notlar }).then(r => r.data),
  deleteSevkiyat: (id) => axiosInstance.delete(`/sevkiyatlar/${id}`).then(r => r.data),
  getSevkiyatKullanicilar: (id) => axiosInstance.get(`/sevkiyatlar/${id}/kullanicilar`).then(r => r.data),
  ataSevkiyatKullanici: (id, kullanici_id) => axiosInstance.post(`/sevkiyatlar/${id}/kullanicilar`, { kullanici_id }).then(r => r.data),
  sevkiyatKullaniciSil: (id, uid) => axiosInstance.delete(`/sevkiyatlar/${id}/kullanicilar/${uid}`).then(r => r.data),
  gecmisVeriCek: (id, saat = 24) => axiosInstance.post(`/sevkiyatlar/${id}/gecmis-veri-cek`, { saat }).then(r => r.data)
};

// Sensör / Cihaz Verisi
export const cihazBilgiService = {
  getSonVeri: (mgz24_kodu) => axiosInstance.get(`/sensor/${mgz24_kodu}`).then(r => r.data),
  getTarihVeri: (mgz24_kodu, params) => axiosInstance.get(`/sensor/${mgz24_kodu}/tarih`, { params }).then(r => r.data)
};

// Cihazlar
export const cihazIDService = {
  getCihazlar: () => axiosInstance.get('/cihazlar').then(r => r.data),
  getInaktifCihazlar: () => axiosInstance.get('/cihazlar/inaktif').then(r => r.data),
  getCihazById: (id) => axiosInstance.get(`/cihazlar/${id}`).then(r => r.data),
  addCihaz: (data) => axiosInstance.post('/cihazlar', data).then(r => r.data),
  updateCihaz: (id, data) => axiosInstance.put(`/cihazlar/${id}`, data).then(r => r.data),
  konturUzat: (id, gun) => axiosInstance.patch(`/cihazlar/${id}/kontor`, { gun }).then(r => r.data),
  getCihazKullanicilar: (id) => axiosInstance.get(`/cihazlar/${id}/kullanicilar`).then(r => r.data),
  ataCihazKullanici: (id, kullanici_id) => axiosInstance.post(`/cihazlar/${id}/kullanicilar`, { kullanici_id }).then(r => r.data),
  cihazKullaniciSil: (id, uid) => axiosInstance.delete(`/cihazlar/${id}/kullanicilar/${uid}`).then(r => r.data)
};

// Kullanıcılar
export const kullaniciService = {
  getKullanicilar: () => axiosInstance.get('/kullanicilar').then(r => r.data),
  getKullanici: (id) => axiosInstance.get(`/kullanicilar/${id}`).then(r => r.data),
  createKullanici: (data) => axiosInstance.post('/kullanicilar', data).then(r => r.data),
  updateKullanici: (id, data) => axiosInstance.put(`/kullanicilar/${id}`, data).then(r => r.data),
  sifreDegistir: (id, data) => axiosInstance.patch(`/kullanicilar/${id}/sifre`, data).then(r => r.data),
  setAktif: (id, aktif) => axiosInstance.patch(`/kullanicilar/${id}/aktif`, { aktif }).then(r => r.data),
  silKullanici: (id) => axiosInstance.delete(`/kullanicilar/${id}`).then(r => r.data)
};

// Bildirimler
export const bildirimService = {
  getBildirimler: () => axiosInstance.get('/bildirimler').then(r => r.data),
  okuOlarak: (id) => axiosInstance.patch(`/bildirimler/${id}/oku`).then(r => r.data),
  tumuOku: () => axiosInstance.patch('/bildirimler/tumu-oku').then(r => r.data)
};

// İzleyici
export const izleyiciService = {
  getIzleyiciler: () => axiosInstance.get('/izleyici/tum-izleyiciler').then(r => r.data),
  getIzledigiSevkiyatlar: () => axiosInstance.get('/izleyici/izledigim-sevkiyatlar').then(r => r.data),
  getSevkiyatlarim: () => axiosInstance.get('/izleyici/sevkiyatlarim').then(r => r.data),
  addIzleyici: (data) => axiosInstance.post('/izleyici', data).then(r => r.data),
  durumDegistir: (id, durum) => axiosInstance.patch(`/izleyici/${id}/durum`, { durum }).then(r => r.data),
  sil: (id) => axiosInstance.delete(`/izleyici/${id}`).then(r => r.data)
};

export default axiosInstance;
